import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { deleteClientAccount, getAllClientAccounts } from "../../../../services/shipping";
import { useParams } from "react-router-dom";
import ThirdPartyShippingAccount from "../../../../components/Shipping/ThirdPartyShippingAccount";

export default function ThirdPartyShippingAccountsTab() {
  const { instanceUniqueId } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [activeTab, setActiveTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedSupplierId, setSelectedSupplierId] = useState("");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await getAllClientAccounts({ instanceUniqueId });
      setAccounts(res?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!instanceUniqueId) return;
    const fetchCustomers = async () => {
      const res = await axios.get(`${SERVER}/customers/get-customers`, {
        params: {
          instanceUniqueId,
          tab: "myCustomers",
          page: 1,
          rpp: 250,
        },
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      setCustomers(res.data?.customers || []);
    };

    const fetchSuppliers = async () => {
      const res = await axios.get(`${SERVER}/suppliers/get-suppliers`, {
        params: {
          instanceUniqueId,
          tab: "connected",
          page: 1,
          pageSize: 250,
        },
        headers: { accessToken: localStorage.getItem("accessToken") },
      });
      setSuppliers(res.data?.rows || []);
    };

    Promise.allSettled([fetchCustomers(), fetchSuppliers()]).catch(() => {});
  }, [SERVER, instanceUniqueId]);

  const filteredAccounts = useMemo(() => {
    if (activeTab === "suppliers") {
      return (accounts || []).filter((account) => account.supplierId);
    }
    return (accounts || []).filter((account) => account.customerId);
  }, [accounts, activeTab]);

  const selectedCustomer = useMemo(
    () => customers.find((customer) => String(customer.id) === String(selectedCustomerId)),
    [customers, selectedCustomerId]
  );

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => String(supplier.Id) === String(selectedSupplierId)),
    [suppliers, selectedSupplierId]
  );

  const canAddAccount =
    activeTab === "suppliers" ? Boolean(selectedSupplierId) : Boolean(selectedCustomerId);

  const modalCustomerId = editingAccount?.customerId
    ? editingAccount.customerId
    : activeTab === "customers"
      ? selectedCustomerId || null
      : null;

  const modalSupplierId = editingAccount?.supplierId
    ? editingAccount.supplierId
    : activeTab === "suppliers"
      ? selectedSupplierId || null
      : null;

  const modalContextName = editingAccount?.customerName
    || editingAccount?.supplierName
    || (activeTab === "suppliers"
      ? selectedSupplier?.Name || ""
      : selectedCustomer?.businessName || selectedCustomer?.emailAddress || "");

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Third Party Shipping Accounts
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage client-provided shipping accounts for this instance.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            Use these for client, supplier, or external accounts you don’t own.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {activeTab === "customers" ? (
            <TextField
              select
              size="small"
              label="Customer"
              value={selectedCustomerId}
              onChange={(event) => setSelectedCustomerId(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Select customer</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.businessName || customer.emailAddress || `Customer #${customer.id}`}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              select
              size="small"
              label="Supplier"
              value={selectedSupplierId}
              onChange={(event) => setSelectedSupplierId(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Select supplier</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.Id} value={supplier.Id}>
                  {supplier.Name || `Supplier #${supplier.Id}`}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="outlined"
            onClick={() => setAccountModalOpen(true)}
            disabled={!canAddAccount && !editingAccount}
          >
            Add Account
          </Button>
          <Button variant="contained" onClick={fetchAccounts}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab value="customers" label="Customer Accounts" />
        <Tab value="suppliers" label="Supplier Accounts" />
      </Tabs>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account Name</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Courier</TableCell>
            <TableCell>Account #</TableCell>
            <TableCell>Billing Postal/Zip</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(filteredAccounts || []).map((account) => (
            <TableRow key={account.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{account.accountName || "—"}</TableCell>
              <TableCell>
                {account.customerName || account.supplierName || "—"}
              </TableCell>
              <TableCell>{account.courier || "—"}</TableCell>
              <TableCell>{account.accountNumber || "—"}</TableCell>
              <TableCell>{account.postalZipCode || "—"}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Edit third party shipping account"
                  onClick={() => {
                    setEditingAccount(account);
                    setAccountModalOpen(true);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete third party shipping account"
                  onClick={async () => {
                    if (!window.confirm("Delete this third party shipping account?")) return;
                    try {
                      await deleteClientAccount(account.id);
                      fetchAccounts();
                    } catch (error) {
                      console.error("Failed to delete third party shipping account", error);
                    }
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (filteredAccounts || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                {activeTab === "suppliers"
                  ? "No supplier third party accounts yet."
                  : "No customer third party accounts yet."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ThirdPartyShippingAccount
        open={accountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
          setEditingAccount(null);
        }}
        instanceUniqueId={instanceUniqueId}
        onCreated={fetchAccounts}
        initialData={editingAccount}
        customerId={modalCustomerId}
        supplierId={modalSupplierId}
        contextLabel={activeTab === "suppliers" ? "this supplier" : "this customer"}
        contextName={modalContextName}
      />
    </Box>
  );
}
